import { useEffect, useRef, useState } from 'react'
import { ChevronRight, Plus, SlidersHorizontal, Trash2, X } from 'lucide-react'
import type {
  StoryChoice,
  StoryCondition,
  StoryEffect,
  StoryProject,
  StoryValue,
  StoryVariable,
  VariableType,
} from '../types'
import {
  conditionProblem,
  defaultValue,
  effectProblem,
  operatorsFor,
  variableTypes,
  variableUsage,
} from '../storyLogic'

export function LogicValueInput({
  type,
  value,
  label,
  onChange,
}: {
  type: VariableType
  value: StoryValue
  label: string
  onChange: (value: StoryValue) => void
}) {
  const [draft, setDraft] = useState(String(value))
  useEffect(() => setDraft(String(value)), [value])
  if (type === 'boolean')
    return (
      <select
        aria-label={label}
        value={String(value)}
        onChange={(event) => onChange(event.target.value === 'true')}
      >
        <option value="false">No</option>
        <option value="true">Yes</option>
      </select>
    )
  if (type === 'number')
    return (
      <input
        aria-label={label}
        type="number"
        step="any"
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value)
          if (
            event.target.value !== '' &&
            Number.isFinite(event.target.valueAsNumber)
          )
            onChange(event.target.valueAsNumber)
        }}
        onBlur={() => setDraft(String(value))}
      />
    )
  return (
    <input
      aria-label={label}
      value={String(value)}
      onChange={(event) => onChange(event.target.value)}
      placeholder="Text value"
    />
  )
}

function VariableSelect({
  variables,
  value,
  label,
  onChange,
}: {
  variables: StoryVariable[]
  value: string
  label: string
  onChange: (variable: StoryVariable) => void
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(event) => {
        const variable = variables.find(
          (item) => item.id === event.target.value,
        )
        if (variable) onChange(variable)
      }}
    >
      {!variables.some((item) => item.id === value) && (
        <option value={value}>Missing variable</option>
      )}
      {variables.map((variable) => (
        <option key={variable.id} value={variable.id}>
          {variable.name || 'Unnamed variable'}
        </option>
      ))}
    </select>
  )
}

export function EffectsEditor({
  variables,
  effects,
  onChange,
  context,
  onOpenVariables,
}: {
  variables: StoryVariable[]
  effects: StoryEffect[]
  onChange: (effects: StoryEffect[]) => void
  context: string
  onOpenVariables: () => void
}) {
  const edit = (id: string, changes: Partial<StoryEffect>) =>
    onChange(
      effects.map((effect) =>
        effect.id === id ? { ...effect, ...changes } : effect,
      ),
    )
  return (
    <div className="logic-effects" role="group" aria-label={context}>
      {effects.map((effect, index) => {
        const variable = variables.find((item) => item.id === effect.variableId)
        const label = `${context}, change ${index + 1}`
        const problem = effectProblem(effect, variables)
        return (
          <div className="logic-rule" key={effect.id}>
            <div className="logic-rule-fields">
              <VariableSelect
                variables={variables}
                value={effect.variableId}
                label={`${label} variable`}
                onChange={(next) =>
                  edit(effect.id, {
                    variableId: next.id,
                    operation: 'set',
                    value: defaultValue(next.type),
                  })
                }
              />
              <div className="logic-comparison">
                <select
                  aria-label={`${label} operation`}
                  value={effect.operation}
                  onChange={(event) =>
                    edit(effect.id, {
                      operation: event.target.value as StoryEffect['operation'],
                    })
                  }
                >
                  <option value="set">Set to</option>
                  {variable?.type === 'number' && (
                    <>
                      <option value="add">Add</option>
                      <option value="subtract">Subtract</option>
                    </>
                  )}
                </select>
                <LogicValueInput
                  key={effect.variableId}
                  type={variable?.type ?? 'text'}
                  value={effect.value}
                  label={`${label} value`}
                  onChange={(value) => edit(effect.id, { value })}
                />
              </div>
              {problem && <small className="logic-problem">{problem}</small>}
            </div>
            <button
              type="button"
              className="icon-button"
              aria-label={`Remove ${label}`}
              onClick={() =>
                onChange(effects.filter((item) => item.id !== effect.id))
              }
            >
              <X size={13} />
            </button>
          </div>
        )
      })}
      {variables.length ? (
        <button
          type="button"
          className="logic-add"
          onClick={() =>
            onChange([
              ...effects,
              {
                id: 'effect-' + crypto.randomUUID(),
                variableId: variables[0].id,
                operation: 'set',
                value: defaultValue(variables[0].type),
              },
            ])
          }
        >
          <Plus size={13} /> Add change
        </button>
      ) : (
        <button type="button" className="logic-add" onClick={onOpenVariables}>
          Add a variable in Story bible <ChevronRight size={13} />
        </button>
      )}
    </div>
  )
}

export function ChoiceLogicEditor({
  project,
  choice,
  choiceNumber,
  onChange,
  onOpenVariables,
}: {
  project: StoryProject
  choice: StoryChoice
  choiceNumber: number
  onChange: (changes: Partial<StoryChoice>) => void
  onOpenVariables: () => void
}) {
  const variables = project.variables ?? []
  const conditions = choice.conditions ?? []
  const effects = choice.effects ?? []
  const edit = (id: string, changes: Partial<StoryCondition>) =>
    onChange({
      conditions: conditions.map((condition) =>
        condition.id === id ? { ...condition, ...changes } : condition,
      ),
    })
  return (
    <details className="choice-logic">
      <summary>
        <SlidersHorizontal size={12} /> Rules & consequences
        {(conditions.length > 0 || effects.length > 0) && (
          <span>
            {conditions.length} {conditions.length === 1 ? 'rule' : 'rules'} ·{' '}
            {effects.length} {effects.length === 1 ? 'change' : 'changes'}
          </span>
        )}
      </summary>
      <div className="logic-body">
        <div className="logic-label">Available when</div>
        {conditions.length > 0 ? (
          <label className="logic-mode">
            <select
              aria-label={`Condition match for choice ${choiceNumber}`}
              value={choice.conditionMode ?? 'all'}
              onChange={(event) =>
                onChange({ conditionMode: event.target.value as 'all' | 'any' })
              }
            >
              <option value="all">All conditions</option>
              <option value="any">Any condition</option>
            </select>{' '}
            are met
          </label>
        ) : (
          <p>
            Always available. Add a condition to make this choice depend on the
            story so far.
          </p>
        )}
        {conditions.map((condition, index) => {
          const variable = variables.find(
            (item) => item.id === condition.variableId,
          )
          const label = `Choice ${choiceNumber}, condition ${index + 1}`
          const problem = conditionProblem(condition, variables)
          return (
            <div className="logic-rule" key={condition.id}>
              <div className="logic-rule-fields">
                <VariableSelect
                  variables={variables}
                  value={condition.variableId}
                  label={`${label} variable`}
                  onChange={(next) =>
                    edit(condition.id, {
                      variableId: next.id,
                      operator: 'eq',
                      value: defaultValue(next.type),
                    })
                  }
                />
                <div className="logic-comparison">
                  <select
                    aria-label={`${label} comparison`}
                    value={condition.operator}
                    onChange={(event) =>
                      edit(condition.id, {
                        operator: event.target
                          .value as StoryCondition['operator'],
                      })
                    }
                  >
                    {operatorsFor(variable?.type ?? 'text').map((operator) => (
                      <option value={operator.value} key={operator.value}>
                        {operator.label}
                      </option>
                    ))}
                  </select>
                  <LogicValueInput
                    key={condition.variableId}
                    type={variable?.type ?? 'text'}
                    value={condition.value}
                    label={`${label} value`}
                    onChange={(value) => edit(condition.id, { value })}
                  />
                </div>
                {problem && <small className="logic-problem">{problem}</small>}
              </div>
              <button
                type="button"
                className="icon-button"
                aria-label={`Remove ${label}`}
                onClick={() =>
                  onChange({
                    conditions: conditions.filter(
                      (item) => item.id !== condition.id,
                    ),
                  })
                }
              >
                <X size={13} />
              </button>
            </div>
          )
        })}
        {variables.length ? (
          <button
            type="button"
            className="logic-add"
            onClick={() =>
              onChange({
                conditions: [
                  ...conditions,
                  {
                    id: 'condition-' + crypto.randomUUID(),
                    variableId: variables[0].id,
                    operator: 'eq',
                    value: defaultValue(variables[0].type),
                  },
                ],
              })
            }
          >
            <Plus size={13} /> Add condition
          </button>
        ) : (
          <button type="button" className="logic-add" onClick={onOpenVariables}>
            Add a variable in Story bible <ChevronRight size={13} />
          </button>
        )}
        {conditions.length > 0 && (
          <label className="logic-mode logic-unavailable">
            Otherwise
            <select
              aria-label={`Unavailable behavior for choice ${choiceNumber}`}
              value={choice.unavailableMode ?? 'disable'}
              onChange={(event) =>
                onChange({
                  unavailableMode: event.target.value as 'hide' | 'disable',
                })
              }
            >
              <option value="disable">Show as locked</option>
              <option value="hide">Hide this choice</option>
            </select>
          </label>
        )}
        <div className="logic-label">When the player chooses this</div>
        <EffectsEditor
          variables={variables}
          effects={effects}
          context={`Choice ${choiceNumber}`}
          onChange={(next) => onChange({ effects: next })}
          onOpenVariables={onOpenVariables}
        />
      </div>
    </details>
  )
}

export function StoryVariables({
  project,
  onChange,
  focusOnOpen = false,
  onSelectNode,
}: {
  project: StoryProject
  onChange: (project: StoryProject, historyKey?: string) => void
  focusOnOpen?: boolean
  onSelectNode: (nodeId: string) => void
}) {
  const variables = project.variables ?? []
  const section = useRef<HTMLDetailsElement>(null)
  const pendingFocus = useRef<string | null>(null)
  useEffect(() => {
    if (focusOnOpen) section.current?.scrollIntoView({ block: 'start' })
  }, [focusOnOpen])
  useEffect(() => {
    if (pendingFocus.current) {
      document.getElementById(pendingFocus.current)?.focus()
      pendingFocus.current = null
    }
  }, [project])
  const edit = (id: string, changes: Partial<StoryVariable>, field: string) =>
    onChange(
      {
        ...project,
        variables: variables.map((variable) =>
          variable.id === id ? { ...variable, ...changes } : variable,
        ),
      },
      `variable:${id}:${field}`,
    )
  return (
    <details
      className="bible-section story-variables"
      ref={section}
      open={focusOnOpen || undefined}
    >
      <summary>
        <SlidersHorizontal size={18} />
        <span>
          Story variables{' '}
          <small>
            {variables.length
              ? `${variables.length} in this story`
              : 'Optional · for choices that remember'}
          </small>
        </span>
        <ChevronRight size={16} />
      </summary>
      <p>
        Track what the player knows, carries, or changes. Use these in choice
        rules and scene changes. Playtests use a separate copy of the starting
        values.
      </p>
      <div className="variable-list">
        {variables.map((variable, index) => {
          const usage = variableUsage(project, variable.id)
          return (
            <article className="variable-card" key={variable.id}>
              <header>
                <input
                  id={'variable-' + variable.id}
                  aria-label={`Variable ${index + 1} name`}
                  value={variable.name}
                  placeholder="e.g. Has the brass key"
                  onChange={(event) =>
                    edit(variable.id, { name: event.target.value }, 'name')
                  }
                />
                <button
                  type="button"
                  className="icon-button danger-action"
                  disabled={usage.length > 0}
                  title={
                    usage.length
                      ? 'Remove its rules and changes before deleting this variable'
                      : 'Remove variable'
                  }
                  aria-label={`Remove variable ${variable.name || index + 1}`}
                  onClick={() =>
                    onChange(
                      {
                        ...project,
                        variables: variables.filter(
                          (item) => item.id !== variable.id,
                        ),
                      },
                      'remove-variable',
                    )
                  }
                >
                  <Trash2 size={14} />
                </button>
              </header>
              <div className="variable-values">
                <label>
                  Type
                  <select
                    aria-label={`Type for variable ${index + 1}`}
                    value={variable.type}
                    disabled={usage.length > 0}
                    onChange={(event) => {
                      const type = event.target.value as VariableType
                      edit(
                        variable.id,
                        { type, initialValue: defaultValue(type) },
                        'type',
                      )
                    }}
                  >
                    {variableTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Starting value
                  <LogicValueInput
                    key={variable.type}
                    type={variable.type}
                    value={variable.initialValue}
                    label={`Starting value for ${variable.name || 'variable ' + (index + 1)}`}
                    onChange={(initialValue) =>
                      edit(variable.id, { initialValue }, 'initial')
                    }
                  />
                </label>
              </div>
              <input
                className="variable-description"
                aria-label={`Description for variable ${index + 1}`}
                value={variable.description}
                placeholder="What does it represent?"
                onChange={(event) =>
                  edit(
                    variable.id,
                    { description: event.target.value },
                    'description',
                  )
                }
              />
              {usage.length > 0 && (
                <div className="variable-usage">
                  <small>
                    Used in {usage.length}{' '}
                    {usage.length === 1 ? 'scene' : 'scenes'}. Remove those
                    rules before changing its type or deleting it.
                  </small>
                  <div>
                    {usage.map((node) => (
                      <button
                        type="button"
                        key={node.id}
                        onClick={() => onSelectNode(node.id)}
                      >
                        {node.title}
                        <ChevronRight size={11} />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </article>
          )
        })}
      </div>
      <button
        type="button"
        className="button button--secondary"
        onClick={() => {
          const id = 'variable-' + crypto.randomUUID()
          pendingFocus.current = 'variable-' + id
          onChange(
            {
              ...project,
              variables: [
                ...variables,
                {
                  id,
                  name: '',
                  type: 'boolean',
                  initialValue: false,
                  description: '',
                },
              ],
            },
            'add-variable',
          )
        }}
      >
        <Plus size={14} /> Add variable
      </button>
    </details>
  )
}
